import {Component, computed, DestroyRef, OnInit, signal, viewChild} from '@angular/core';
import {PanelComponent} from '../../../../panel/panel.component';
import {Table, TableLazyLoadEvent, TableModule, TableRowSelectEvent} from 'primeng/table';
import {VehicleListFacade} from '../facades/vehicle-list.facade';
import {AsyncPipe} from '@angular/common';
import {Paginator} from 'primeng/paginator';
import {VehicleSearch} from '../models/vehicle-search.model';
import {Router} from '@angular/router';
import {Button} from 'primeng/button';
import {Tooltip} from 'primeng/tooltip';
import {AppTextInputComponent} from '../../../../../shared/components/input/text-input/text-input.component';
import {FormsModule} from '@angular/forms';
import {VehicleSearchFilterRequest} from '../models/vehicle-search-filter-request.model';
import {PageableRequest, PaginationSorting} from '../../../../../core/models/pagable.model';
import {
  MultiSelectInputComponent,
  MultiSelectRawSelectionChangeEvent
} from '../../../../../shared/components/input/multiselect-input/multiselect-input.component';
import {AvailableVehicleCategory} from '../../../category/enum/available-vehicle-category.enum';
import {Brand} from '../../../brand/models/brand.model';
import {AppDateInputComponent} from '../../../../../shared/components/input/date-input/date-input.component';
import {PaginationSortDirection} from '../../../../../core/enums/pagination/pagination-sort-direction.enum';
import {DateTime} from 'luxon';
import {debounceTime} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-vehicles-list',
  imports: [
    PanelComponent,
    TableModule,
    AsyncPipe,
    Paginator,
    Button,
    Tooltip,
    AppTextInputComponent,
    FormsModule,
    MultiSelectInputComponent,
    AppDateInputComponent
  ],
  templateUrl: './vehicles-list.component.html',
  styleUrl: './vehicles-list.component.css',
})
export class VehiclesListComponent implements OnInit {
  protected readonly returnDateFrom = signal<DateTime | null>(null);
  protected readonly returnDateTo = signal<DateTime | null>(DateTime.now());
  protected readonly releaseDateFrom = signal<DateTime | null>(null);
  protected readonly releaseDateTo = signal<DateTime | null>(DateTime.now());
  protected readonly brandsSelected = computed(() => this.filteredBrands().length > 0);
  private readonly filteredBrands = signal<Brand[]>([])
  private readonly tableElement = viewChild<Table>('vehicleSearchTable');
  private readonly currentSortField = signal<string | null>(null);
  private readonly currentSortOrder = signal<number>(0);

  constructor(
    private readonly facade: VehicleListFacade,
    private readonly router: Router,
    private readonly destroyRef: DestroyRef) {
  }

  public ngOnInit(): void {
    this.facade.loadFirstPage();
    this.registerLazyLoadTableEventCallback();
  }

  protected get orchestrator(): VehicleListFacade {
    return this.facade;
  }

  protected onRowSelect($event: TableRowSelectEvent<VehicleSearch>) {
    if (!$event.data) return;
    const vehicle: VehicleSearch | VehicleSearch[] = $event.data;
    if (vehicle instanceof VehicleSearch) {
      this.router.navigate(['/vehicles/', vehicle.id]);
    }

    if (vehicle instanceof Array) {
      vehicle.slice(0, 1).forEach(
        vehicle => this.router.navigate(['/vehicles/', vehicle.id])
      )
    }
  }

  protected onAddVehicle() {
    this.router.navigate(['/vehicles/create']);
  }

  protected onFilter(event: TableLazyLoadEvent): void {
    this.facade.lazyLoadTableSubject.next(event);
  }

  protected processLazyLoadEvent(event: TableLazyLoadEvent): void {
    const newField = event.sortField as string ?? null;
    const newOrder = event.sortOrder ?? 0;

    const prevField = this.currentSortField();
    const prevOrder = this.currentSortOrder();

    if (newField && newField === prevField && prevOrder === -1 && newOrder === 1) {
      this.currentSortField.set(null);
      this.currentSortOrder.set(0);
      this.facade.setSort(undefined);

      const table = this.tableElement();

      if (table) {
        table.sortField = undefined;
        table.sortOrder = 0;
      }
    } else {
      this.currentSortField.set(newField);
      this.currentSortOrder.set(newOrder);

      const sort = this.buildSort(newField, newOrder !== 0 ? newOrder : undefined);
      this.facade.setSort(sort);
    }

    const filters = this.extractFilters(event.filters ?? {});
    const pageable = new PageableRequest(
      this.facade.currentPage(),
      this.facade.pageSize(),
      this.facade.sort()
    );

    this.facade.loadPage(pageable, filters);
  }

  protected onBrandFilterShow(): void {
    this.facade.fetchVehicleBrands();
  }

  protected onBrandFilterChange($event: MultiSelectRawSelectionChangeEvent<Brand>): void {
    this.filteredBrands.set($event.value);
  }

  protected onModelFilterShow(): void {
    const brandIds = this.filteredBrands().map(brand => brand.id);
    this.facade.fetchVehicleModels(brandIds);
  }

  protected onBrandFilterHide(): void {
    if (!this.facade.activeFilters().brand?.length) {
      this.filteredBrands.set([]);
      this.facade.clearVehicleModels();

      const table = this.tableElement();

      if (table?.filters['model']) {
        delete table.filters['model'];
        table._filter()
      }
    }
  }

  protected onApplyReturnDateFilter(): void {
    const table = this.tableElement();
    if (!table) return;

    const from = this.returnDateFrom()?.toISO({includeOffset: false});
    const to = this.returnDateTo()?.toISO({includeOffset: false});

    if (from || to) {
      table.filters['returnDate'] = [{value: {from, to}, matchMode: 'in'}];
    }

    table._filter();
  }

  protected onClearReturnDateFilter(): void {
    this.returnDateFrom.set(null);
    this.returnDateTo.set(DateTime.now());

    const table = this.tableElement();
    if (!table) return;

    table.filters['returnDate'] = [{value: null, matchMode: 'in'}];
    table._filter();
  }

  protected onApplyReleaseDateFilter() {
    const table = this.tableElement();
    if (!table) return;

    const from = this.releaseDateFrom()?.toISO({includeOffset: false});
    const to = this.releaseDateTo()?.toISO({includeOffset: false});

    if (from || to) {
      table.filters['releaseDate'] = [{value: {from, to}, matchMode: 'in'}];
    }

    table._filter();
  }

  protected onClearReleaseDateFilter() {
    this.releaseDateFrom.set(null);
    this.releaseDateTo.set(DateTime.now());

    const table = this.tableElement();
    if (!table) return;

    table.filters['releaseDate'] = [{value: null, matchMode: 'in'}];
    table._filter();
  }

  private registerLazyLoadTableEventCallback(): void {
    this.facade.lazyLoadTableSubject.pipe(
      debounceTime(250),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(event => this.processLazyLoadEvent(event));
  }

  private getFilterRange(meta: any): { from: string; to: string } | null {
    if (!meta) return null;

    const arr = Array.isArray(meta) ? meta : [meta];
    const active = arr.find(f => f.value !== null && f.value !== '');

    return active?.value ?? null;
  }

  private getFilterValue(meta: any): string | null {
    if (!meta) return null;

    const arr = Array.isArray(meta) ? meta : [meta];
    const active = arr.find(f => f.value !== null && f.value !== '');

    return active?.value ?? null;
  }

  private getFilterArray<T = string>(meta: any): T[] | null {
    if (!meta) return null;
    const arr = Array.isArray(meta) ? meta : [meta];
    const active = arr.find(f => f.value !== null && f.value !== '');
    if (!active?.value) return null;
    return Array.isArray(active.value) ? active.value as T[] : [active.value as T];
  }

  private getFilterNumericValue(meta: any): number | null {
    const value = this.getFilterValue(meta);
    if (!value) return null;
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  }

  private buildSort(sortField: string | undefined, sortOrder: number | undefined): PaginationSorting | undefined {
    if (!sortField || !sortOrder) {
      return undefined;
    }

    return {
      field: sortField,
      direction: sortOrder === 1 ? PaginationSortDirection.ASC : PaginationSortDirection.DESC
    };
  }

  private extractFilters(rawFilters: Record<string, any>): VehicleSearchFilterRequest {
    const returnDateRange = this.getFilterRange(rawFilters['returnDate']);
    const releaseDateRange = this.getFilterRange(rawFilters['releaseDate']);

    return new VehicleSearchFilterRequest(
      this.getFilterArray(rawFilters['brand']),
      this.getFilterArray(rawFilters['model']),
      this.getFilterArray<AvailableVehicleCategory>(rawFilters['category']),
      this.getFilterValue(rawFilters['vin']),
      this.getFilterValue(rawFilters['factoryNumber']),
      this.getFilterNumericValue(rawFilters['documentNumber']),
      this.getFilterNumericValue(rawFilters['keyNumber']),
      this.getFilterValue(rawFilters['registrationPlate']),
      returnDateRange?.from ?? null,
      returnDateRange?.to ?? null,
      releaseDateRange?.from ?? null,
      releaseDateRange?.to ?? null
    );
  }
}
